
import { mapActions, mapState } from 'vuex'
import { mdiArrowRight, mdiRvTruck } from '@mdi/js'
import utils from '@/components/tools/utils'
import SimpleCountDown from '@/components/catalog/product/SimpleCountDown.vue'

export default {
  name: 'AvailabilityConfirmationModal',

  components: { SimpleCountDown },

  icons: {
    mdiArrowRight,
    mdiRvTruck
  },

  data () {
    return {
      dialog: false,
      product: undefined
    }
  },

  computed: {
    ...mapState('customer', ['needAvailabilityConfirmation']),

    availabilityConfirmation () {
      return this.product?.auction?.availabilityConfirmation
    },
    endingAt () {
      return this.availabilityConfirmation?.endingAt
    },
    title () {
      return this.product?.title || this.$t('no_title')
    },
    imageUrl () {
      return this.product?.image
    },
    firstRegistration () {
      // getTrailerFirstRegistration() is @JsonIgnore'd on the backend, so for a
      // trailer the date only ever arrives inside the raw EAV datetimes bucket.
      return this.product?.firstRegistration ||
        this.product?.attributes?.datetimes
          ?.find(a => a.attributeCode === 'trailer_first_registration')?.value
    },
    meta () {
      const parts = []

      if (this.firstRegistration) {
        parts.push(this.$t('availability.confirmation.first_registration', [
          utils.formatDateMini(this.firstRegistration)
        ]))
      }

      if (this.product?.mileage !== null && this.product?.mileage !== undefined) {
        parts.push(this.$t('availability.confirmation.mileage', [
          utils.formatNumber(this.product.mileage)
        ]))
      }

      return parts.join(' · ')
    },
    confirmationRoute () {
      return this.localePath({
        name: 'availability-auctionId-uuid',
        params: {
          auctionId: this.product?.auction?.auctionId,
          uuid: this.availabilityConfirmation?.uuid
        }
      })
    },
    onConfirmationPage () {
      return this.$route.name?.startsWith('availability-auctionId-uuid')
    }
  },

  watch: {
    // getCustomer() runs on every full page load (layout fetch) and again after the
    // seller answers on the stepper, so this flag is the only thing that decides
    // whether the product has to be fetched. No request on route changes.
    needAvailabilityConfirmation: {
      immediate: true,
      handler (needed) {
        if (needed) {
          this.load()
        } else {
          this.dialog = false
        }
      }
    },

    // Back button from the stepper to any other page: show the modal again from
    // the product already loaded. Store only, no request.
    '$route.path' () {
      this.sync()
    }
  },

  methods: {
    ...mapActions('customer', ['getCustomer']),
    onCountdownFinish () {
      setTimeout(async () => {
        this.dialog = false
        this.product = null
        if (this.$auth.loggedIn) {
          await this.getCustomer()
        }
        await this.load()
      }, 2000)
    },

    async load () {
      if (this.product) {
        this.sync()
        return
      }

      try {
        const {
          status,
          data
        } = await this.$axios.get('/private/availability/customer/get', {
          meta: { suppressGlobalError: true }
        })

        // 204 when nothing is pending for this customer.
        if (status === 200 && data?.auction?.availabilityConfirmation?.status === 'needs_confirmation') {
          this.product = data
        }
      } catch (e) {
        // A failed lookup must never break the page it is layered on.
        console.error(e)
      }

      this.sync()
    },

    /**
     * The stepper is the only place where the confirmation can be given, so it is
     * never covered by the modal that points to it. Everywhere else the modal stays
     * up until the flag is cleared.
     */
    sync () {
      this.dialog = Boolean(this.needAvailabilityConfirmation && this.product && !this.onConfirmationPage)
    },

    thumb (quality) {
      return this.$img(this.imageUrl, {
        width: 224,
        height: 160,
        quality
      }, { provider: 'imagekit' })
    }
  }
}
